import { DownloadKitButtonSelectorView } from '../view/DownloadKitButtonSelectorView';
import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import { DownloadKitButtonSelectorWebComponent } from '../component/DownloadKitButtonSelectorWebComponent';
import { DownloadKitButtonSelectorRepository } from '../repository/DownloadKitButtonSelectorRepository';
import { templateDownloadKitButtonSelector } from '../view/template';
import { PackageType } from '../../packagesGrid/types';
import { DownloadCallback, DownloadResponse, KitItem, MegaMenuSection } from '../types/types';
import {
  ALWAYS_FULL_KIT,
  ALWAYS_NORMAL_KIT,
  DownloadTypes,
  DownloadTypesDbName,
  KIT_STATUSES_CLASS_MAP,
  KitIds,
  kitIdsToDbName,
  KitStatuses,
  IconMap,
  RingDownloadTypes,
  availableSections,
  dbNameToKitIds,
} from '../constants';
import { Feature } from '@nirvana/shared/constants';
import { SelectEvents } from '../../../ui/gzSelect/types';
import { GzToast, ToastPositionX, ToastPositionY, ToastSeverity } from '../../../ui/gzToastNotification/type';
import { GzToastNotificationWebComponent } from '../../../ui/gzToastNotification';
import {
  OnDownloadClicked,
  OnDownloadK8sAgent,
  OnDownloadSecurityContainer,
  OnDownloadStarted,
} from '../events/events';
import { DataProviderType } from '../../../utils/request';
import { MonthlyProductType } from '../../../license/types/MonthlyProductType';
import { translateFn } from '../../../utils/translator.helper';
import { MegaMenuEvents } from '../../../ui/gzMegaMenu/types';
import { indexOf } from 'lodash';

export class DownloadKitButtonSelectorController extends BaseWebComponentController {
  constructor(component: DownloadKitButtonSelectorWebComponent) {
    super(component);

    this.initialSetup({
      viewConstructor: DownloadKitButtonSelectorView,
      viewTemplate: templateDownloadKitButtonSelector,
      dataRepositoryConstructor: DownloadKitButtonSelectorRepository,
    });

    this.packageTypeToKits = new Map<PackageType, Array<KitItem>>();
    this.setBestKits();
    this.setSVaKits();
    this.setSandboxKits();
    this.setGhostKits();
    this.setNSVAKits();
    this.setFratKits(PackageType.Frat);
    this.setFratKits(PackageType.RelayForFrat);
    this.setKubernetesKits();
  }

  public component: DownloadKitButtonSelectorWebComponent;
  public view: DownloadKitButtonSelectorView;
  public viewConstructor: typeof DownloadKitButtonSelectorView;
  public dataRepository: DownloadKitButtonSelectorRepository;
  public dataRepositoryConstructor: typeof DownloadKitButtonSelectorRepository;
  private packageTypeToKits: Map<number, Array<KitItem>>;
  public pinnedVersions: any;
  public dbAvailableVersions: any;
  public unavailableMessage = translateFn('Kit unavailable for this ring. Check staging settings.');

  //methods should always return bool
  private stringToValidationMethods: { [key: string]: (...args: any[]) => boolean } = {
    isOnprem: this.isOnprem.bind(this),
    isPackageDefaultProduct: this.isPackageDefaultProduct.bind(this),
    packageCompanyHasContainerProtectionUsable: this.packageCompanyHasContainerProtectionUsable.bind(this),
    isPinnedVersionAvailableForRing: this.isPinnedVersionAvailableForRing.bind(this),
    hasAccessToNSVAPackages: this.hasAccessToNSVAPackages.bind(this),
  };

  /**
   *
   * @param message
   * @param severity
   * @private
   */
  private static createNotification(severity: ToastSeverity, message: string = ''): void {
    const notificationConfig = {
      message: message,
      hasAction: false,
      severity: severity,
    };
    GzToastNotificationWebComponent.buildToastWrapperElement(notificationConfig);
  }

  /**
   *
   * @param packageId
   * @param downloadType
   * @private
   */
  private static startDownloadFullKit(packageId: string, downloadType: number): void {
    const url = app.getDataProviderUpload(DataProviderType.CORE, 'Packages', 'downloadKit');
    const oldForm = document.body.querySelector('#downloadFrom');

    if (oldForm) {
      oldForm.remove();
    }

    const form = document.createElement('form');
    form.setAttribute('method', 'POST');
    form.setAttribute('action', url);
    form.setAttribute('id', 'downloadFrom');

    const params = document.createElement('input');
    const paramsEncoded = JSON.stringify({
      packageId: packageId,
      downloadType: downloadType,
    });
    params.setAttribute('name', 'params');
    params.setAttribute('value', paramsEncoded);
    form.appendChild(params);
    document.body.appendChild(form);

    form.submit();
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$megaMenuContainer,
        event: MegaMenuEvents.ITEM_CLICKED,
        callback: this.onDownloadSelected,
      },
      {
        element: this.view.dom.$megaMenuButton,
        event: 'click',
        callback: this.onDownloadClickedMM,
      },
    ];
  }

  private async onDownloadClickedMM(): Promise<void> {
    // set icon map to be used by mega menu
    this.view.dom.$megaMenuContainer.iconMap = IconMap;

    // get package details. set by packages grid.
    const packageDetails = this.component.getPackageDetails();

    // get package ring versions for package company
    const pinnedAndDbVersions = await this.dataRepository.getPackageCompanyRingVersions(packageDetails.company.id);
    this.pinnedVersions = pinnedAndDbVersions.pinnedVersions;
    this.dbAvailableVersions = pinnedAndDbVersions.dbAvailableVersions;

    const packageType = this.component.getPackageType();
    const options = this.packageTypeToKits.get(packageType);
    if (!options) {
      return;
    }
    const availableKits: Array<number> = [];
    const availableOptions: Array<any> = [];
    this.view.dom.$megaMenuContainer.sections = []; // init mega menu sections

    options.forEach(kit => {
      if (!this.isKitVisible(kit)) {
        return;
      }

      availableKits.push(kit.downloadType);
      availableOptions.push(kit);
    });

    let statuses = {};
    try {
      statuses = await this.dataRepository.getDownloadTypeStatuses(availableKits, this.component.getOemKitId());
    } catch (e) {
      //if any error occurred catch the exception
      //all the kit's will have the error icon
      console.error(e);
    }

    this.view.dom.$megaMenuContainer.sections = this.parseAvailableOptions(availableOptions, statuses, packageType);

    this.changeOverlayOpen(true);
  }
  private async changeOverlayOpen(open): Promise<void> {
    const overlay = this.view.dom.$megaMenuOverlay;
    if (overlay) overlay.open = open;
  }

  private async onDownloadSelected(ev: WebComponentEvent): Promise<void> {
    this.changeOverlayOpen(false);

    const downloadType = ev.detail.selected;
    const packageId = this.component.getPackageId();
    const hasProxy = this.component.getHasProxy();

    this.component.dispatchEvent(new OnDownloadClicked());

    try {
      if (downloadType === DownloadTypes.DOWNLOAD_SECURITY_CONTAINER) {
        await this.downloadNormalKit(packageId, downloadType, this.showDownloadContainerWindow.bind(this));
        return;
      }

      if (downloadType === DownloadTypes.DOWNLOAD_K8S_AGENT) {
        await this.downloadNormalKit(packageId, downloadType, this.showDownloadK8sAgentWindow.bind(this));
        return;
      }

      if (ALWAYS_FULL_KIT.indexOf(downloadType) > -1) {
        await this.downloadFullKit(packageId, downloadType);
        return;
      }

      if (ALWAYS_NORMAL_KIT.indexOf(downloadType) > -1) {
        await this.downloadNormalKit(packageId, downloadType, this.startDownloadNormalKit);
        return;
      }

      if (hasProxy) {
        await this.downloadFullKit(packageId, downloadType);
      } else {
        await this.downloadNormalKit(packageId, downloadType, this.startDownloadNormalKit);
      }
      return;
    } catch (error) {
      this.createErrorToastNotification(error.err.exceptionType);
      this.component.dispatchEvent(new OnDownloadStarted());
      return;
    }
  }

  private createErrorToastNotification(exception: string): void {
    let errorMessage: string;
    switch (exception) {
      case 'Exceptions\\FullKitDownloadLimitException':
        errorMessage = translateFn(
          'You have reached the full installation kit download limit. Please wait 60 seconds before trying again.'
        );
        break;
      case 'Exceptions\\DownloadNotStartedKitsException':
        errorMessage = translateFn('Could not start download for all kits. Available version information missing.');
        break;
      default:
        errorMessage = translateFn('Package download error');
        break;
    }
    DownloadKitButtonSelectorController.createNotification(ToastSeverity.ERROR, errorMessage);
  }

  private isKitDisabled(item, statuses): boolean {
    let isDisabled = false;

    if (indexOf(RingDownloadTypes, item.downloadType) !== -1) {
      // put all rings in array this is executed for rings download type
      isDisabled = !this.isPinnedVersionAvailableForRing({
        dbKitName: kitIdsToDbName[item.kitId],
        ring: item.downloadType,
      });
    }

    return isDisabled;
  }

  private getItemIcon(item, statuses): string {
    if (statuses[item.downloadType]) {
      return KIT_STATUSES_CLASS_MAP[statuses[item.downloadType]];
    }
    return KIT_STATUSES_CLASS_MAP[KitStatuses.STATUS_ERROR];
  }

  private parseAvailableOptions(availableOptions, statuses, packageType) {
    const sectionsArray: any = [];

    availableSections.WinSection.items = [];
    availableSections.NixSection.items = [];
    availableSections.MacSection.items = [];
    availableSections.SvaSection.items = [];
    availableSections.FratSection.items = [];
    availableSections.SandboxSection.items = [];
    availableSections.GhostSection.items = [];
    availableSections.NsvaSection.items = [];
    availableSections.K8sSection.items = [];

    availableOptions.forEach(item => {
      item.disabled = this.isKitDisabled(item, statuses);
      item.icon = this.getItemIcon(item, statuses);

      switch (item.downloadType) {
        case DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_WIN:
        case DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_WIN32:
        case DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_x64:
        case DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_ARM:
          if (packageType === PackageType.Frat || packageType === PackageType.RelayForFrat) {
            availableSections.FratSection.items.push(item);
            break;
          }

          availableSections.WinSection.items.push(item);
          break;

        case DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_WIN_PRODUCTION_RING:
        case DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_WIN_TEST1_RING:
        case DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_WIN_TEST2_RING:
          availableSections.WinSection.items.push(item);
          break;

        case DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_LINUX:
        case DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_LINUX_PRODUCTION_RING:
        case DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_LINUX_TEST1_RING:
        case DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_LINUX_TEST2_RING:
        case DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_NIX_x32:
        case DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_NIX_x64:
        case DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_NIX_ARM:
        case DownloadTypes.DOWNLOAD_SECURITY_CONTAINER:
          availableSections.NixSection.items.push(item);
          break;

        case DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_MAC:
        case DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_MAC_PRODUCTION_RING:
        case DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_MAC_TEST1_RING:
        case DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_MAC_TEST2_RING:
        case DownloadTypes.DOWNLOAD_MAC_FULL_KIT_X86:
        case DownloadTypes.DOWNLOAD_MAC_FULL_KIT_ARM:
          availableSections.MacSection.items.push(item);
          break;

        case DownloadTypes.DOWNLOAD_SVA_VMWARE_WITHOUT_VCENTER_OVA:
        case DownloadTypes.DOWNLOAD_SVA_VMWARE_OVA:
        case DownloadTypes.DOWNLOAD_SVA_CITRIX_XEN_XVA:
        case DownloadTypes.DOWNLOAD_SVA_MICROSOFT_HYPER_V:
        case DownloadTypes.DOWNLOAD_SVA_MICROSOFT_HYPER_V_GEN2:
        case DownloadTypes.DOWNLOAD_SVA_NUTANIX_VMDK:
          availableSections.SvaSection.items.push(item);
          break;

        case DownloadTypes.DOWNLOAD_SANDBOX_ANALYZER:
          availableSections.SandboxSection.items.push(item);
          break;

        case DownloadTypes.DOWNLOAD_GHOSTR_SENSOR:
          availableSections.GhostSection.items.push(item);
          break;

        case DownloadTypes.DOWNLOAD_NSVA_VMWARE_OVA:
        case DownloadTypes.DOWNLOAD_NSVA_MICROSOFT_HYPER_V:
        case DownloadTypes.DOWNLOAD_NSVA_MICROSOFT_HYPER_V_GEN2:
          availableSections.NsvaSection.items.push(item);
          break;

        case DownloadTypes.DOWNLOAD_K8S_AGENT:
          availableSections.K8sSection.items.push(item);
          break;
      }
    });

    Object.keys(availableSections).forEach(key => {
      if (availableSections[key].items.length > 0) {
        sectionsArray.push(availableSections[key]);
      }
    });

    return sectionsArray;
  }

  /**
   *
   * @param packageId
   * @param downloadType
   * @private
   */
  private async downloadFullKit(packageId: string, downloadType: number): Promise<void> {
    const response = await this.dataRepository.checkAccessToPackage(packageId, downloadType);
    this.component.dispatchEvent(new OnDownloadStarted());
    if (response.status === KitStatuses.STATUS_READY) {
      DownloadKitButtonSelectorController.startDownloadFullKit(packageId, downloadType);
    } else {
      this.showDownloadedStatusWindow(response.status, response.isImage);
    }
  }

  private async downloadNormalKit(
    packageId: string,
    downloadType: number,
    downloadCallback: DownloadCallback
  ): Promise<void> {
    const kit = await this.dataRepository.download(packageId, downloadType);
    this.component.dispatchEvent(new OnDownloadStarted());

    if (kit.status === KitStatuses.STATUS_READY) {
      downloadCallback(kit);
    } else {
      this.showDownloadedStatusWindow(kit.status, kit.isImage);
    }
  }

  private startDownloadNormalKit(kit: DownloadResponse): void {
    const url = kit.setupUrl,
      link = document.createElement('a');
    link.setAttribute('href', url);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  private showDownloadContainerWindow(): void {
    this.component.dispatchEvent(new OnDownloadSecurityContainer(this.component.getPackageId()));
  }

  private showDownloadK8sAgentWindow(): void {
    this.component.dispatchEvent(new OnDownloadK8sAgent());
  }

  /**
   *
   * @param status
   * @param isImage
   * @private
   */
  private showDownloadedStatusWindow(status: KitStatuses, isImage: boolean): void {
    const message = this.getPackageStatusMessage(status, isImage);
    const hasAction = status === KitStatuses.STATUS_DOWNLOADING && this.dependencies.permissions.canManagePlatform;
    const toastNotificationConfigs: GzToast = {
      message,
      severity: ToastSeverity.INFORMATIVE,
      hasAction: hasAction,
      x: ToastPositionX.RIGHT,
      y: ToastPositionY.BOTTOM,
      autoCloseDelayMs: 10000,
      actionButtonText: hasAction ? translateFn('Configuration > Update > Components') : null,
      actionCallback: hasAction
        ? () => {
            this.dependencies.GoToComponentsPanel();
          }
        : null,
      showInlineButton: true,
    };

    GzToastNotificationWebComponent.buildToastWrapperElement(toastNotificationConfigs);
  }

  /**
   *
   * @param status
   * @param isImage
   * @private
   */
  private getPackageStatusMessage(status: KitStatuses, isImage: boolean): string {
    let message = '';

    if (status === KitStatuses.STATUS_DOWNLOADING && isImage) {
      message = this.dependencies.translateFn(
        'Please wait until the Security Server image downloads from the Bitdefender repository. You can view the download status here:'
      );
      if (!this.dependencies.permissions.canManagePlatform) {
        message = this.dependencies.translateFn(
          'Please wait until the Security Server image downloads from the Bitdefender repository. When finished, you can view a check mark in the Download menu, next to the requested image.'
        );
      }
    } else if (status === KitStatuses.STATUS_DOWNLOADING && !isImage) {
      message = this.dependencies.translateFn(
        'Please wait until the kit downloads from the Bitdefender repository. You can view the download status here:'
      );
      if (!this.dependencies.permissions.canManagePlatform) {
        message = this.dependencies.translateFn(
          'Please wait until the kit downloads from the Bitdefender repository. When finished, you can view a check mark in the Download menu, next to the requested kit.'
        );
      }
    }

    if (status === KitStatuses.STATUS_NOT_DOWNLOADED && isImage) {
      message = this.dependencies.translateFn(
        'The desired Security Server image is not available and the auto download option is disabled!'
      );
      if (!this.dependencies.permissions.canManagePlatform) {
        message = this.dependencies.translateFn(
          'The requested Security Server image is not available. Contact a user with Company Administrator role in GravityZone to download the image from the Bitdefender repository.'
        );
      }
    } else if (status === KitStatuses.STATUS_NOT_DOWNLOADED && !isImage) {
      message = this.dependencies.translateFn(
        'The desired kit is not available and the auto download option is disabled!'
      );
      if (!this.dependencies.permissions.canManagePlatform) {
        message = this.dependencies.translateFn(
          'The requested kit is not available. Contact a user with Company Administrator role in GravityZone to download the kit from the Bitdefender repository.'
        );
      }
    }

    return message;
  }

  public enableDownloadButton(enabled: boolean): void {
    if (enabled) {
      this.view.dom.$megaMenuButton.removeAttribute('disabled');
    } else {
      this.view.dom.$megaMenuButton.setAttribute('disabled', 'true');
    }
  }

  private isOnprem(): boolean {
    return this.dependencies.isGZ();
  }

  /**
   * Validates if kit should be displayed or not
   * it has to type of validations:
   * 1) Feature validation -> check that the use has visibility to feature
   * 2) Methods check validation -> iterates throw all the kit.dependencies?.methods
   *    if all the responses are true then the kit is visible . If only one call to method is false the kit should not be visible
   *
   * @param kit
   * @private
   */
  private isKitVisible(kit: KitItem): boolean {
    let kits: Array<number> | null = [];

    if (typeof kit.dependencies.feature === 'number') {
      kits.push(kit.dependencies.feature);
    }

    if (typeof kit.dependencies.feature === 'object') {
      kits = kit.dependencies.feature;
    }

    if (kits !== null && kits.length !== 0 && !this.dependencies.userHasAllFeatures(kits)) {
      return false;
    }

    let isVisible = true;
    if (kit.dependencies?.methods) {
      kit.dependencies.methods.every(method => {
        if (!this.stringToValidationMethods[method]) {
          isVisible = false;
          return false;
        }

        const params = kit.dependencies.parameters ? kit.dependencies.parameters[method] : null;

        if (!this.stringToValidationMethods[method](params)) {
          isVisible = false;
          return false;
        }

        return true;
      });
    }

    return isVisible;
  }

  private isPackageDefaultProduct(): boolean {
    return MonthlyProductType.EndpointSecurity === this.component.getPackageProductType();
  }

  private hasAccessToNSVAPackages(): boolean {
    return (
      this.dependencies.userHasFeature(Feature.PRODUCT_FEATURE_NETWORK_SENSORS) ||
      this.dependencies.userHasFeature(Feature.PRODUCT_FEATURE_NETWORK_SENSORS_USABLE_DELEGATE)
    );
  }

  private isPinnedVersionAvailableForRing(params) {
    if (params === null || params === undefined) {
      return false;
    }

    if (
      params.dbKitName &&
      this.pinnedVersions &&
      this.pinnedVersions[params.dbKitName] !== undefined &&
      this.pinnedVersions[params.dbKitName][DownloadTypesDbName[params['ring']]] !== undefined
    ) {
      if (
        this.dbAvailableVersions &&
        this.dbAvailableVersions[dbNameToKitIds[params.dbKitName]] !== undefined &&
        this.dbAvailableVersions[dbNameToKitIds[params.dbKitName]][
          this.pinnedVersions[params.dbKitName][DownloadTypesDbName[params['ring']]]
        ] === true
      ) {
        return true;
      }

      return false;
    }

    return false;
  }

  /**
   * check if the company for which the package was created hasContainerProtectionUsable
   * @private
   */
  private packageCompanyHasContainerProtectionUsable(): boolean {
    return this.component.getPackageCompanySettings()?.hasContainerProtectionUsable;
  }

  private setBestKits(): void {
    this.packageTypeToKits.set(PackageType.Endpoints, [
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_WIN,
        kitId: KitIds.DOWNLOAD_UNIFIED_DOWNLOADER_WIN,
        name: 'Downloader',
        downloadType: DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_WIN,
        dependencies: {
          feature: null,
          methods: null,
        },
        disabledMessage: '',
        label: translateFn('Downloader'),
        event: { selected: DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_WIN },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_WIN_PRODUCTION_RING,
        kitId: KitIds.DOWNLOAD_UNIFIED_DOWNLOADER_WIN,
        name: 'Downloader for Production ring',
        downloadType: DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_WIN_PRODUCTION_RING,
        dependencies: {
          feature: Feature.PRODUCT_FEATURE_RING_STAGING_VISIBLE,
          methods: null,
          parameters: null,
        },
        disabledMessage: this.unavailableMessage,
        label: translateFn('Downloader for Production ring'),
        event: { selected: DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_WIN_PRODUCTION_RING },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_WIN_TEST1_RING,
        kitId: KitIds.DOWNLOAD_UNIFIED_DOWNLOADER_WIN,
        name: 'Downloader for Test ring 1',
        downloadType: DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_WIN_TEST1_RING,
        dependencies: {
          feature: [Feature.PRODUCT_FEATURE_RING_STAGING_VISIBLE],
          methods: null,
          parameters: null,
        },
        disabledMessage: this.unavailableMessage,
        label: translateFn('Downloader for Test ring 1'),
        event: { selected: DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_WIN_TEST1_RING },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_WIN_TEST2_RING,
        kitId: KitIds.DOWNLOAD_UNIFIED_DOWNLOADER_WIN,
        name: 'Downloader for Test ring 2',
        downloadType: DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_WIN_TEST2_RING,
        dependencies: {
          feature: [Feature.PRODUCT_FEATURE_RING_STAGING_VISIBLE],
          methods: null,
          parameters: null,
        },
        disabledMessage: this.unavailableMessage,
        label: translateFn('Downloader for Test ring 2'),
        event: { selected: DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_WIN_TEST2_RING },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_WIN32,
        kitId: KitIds.DOWNLOAD_UNIFIED_FULL_KIT_WIN32,
        name: 'Kit (Intel, AMD x86 32-bit)',
        downloadType: DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_WIN32,
        dependencies: {
          feature: null,
          methods: null,
        },
        disabledMessage: '',
        label: translateFn('Kit (Intel, AMD x86 32-bit)'),
        event: { selected: DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_WIN32 },
        disabled: false,
      },

      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_x64,
        kitId: KitIds.DOWNLOAD_UNIFIED_FULL_KIT_x64,
        name: 'Kit (Intel, AMD x86 64-bit)',
        downloadType: DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_x64,
        dependencies: {
          feature: null,
          methods: null,
        },
        disabledMessage: '',
        label: translateFn('Kit (Intel, AMD x86 64-bit)'),
        event: { selected: DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_x64 },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_ARM,
        kitId: KitIds.DOWNLOAD_UNIFIED_FULL_KIT_ARM,
        name: 'Kit (ARM 64-bit)',
        downloadType: DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_ARM,
        dependencies: {
          feature: null,
          methods: null,
        },
        disabledMessage: '',
        label: translateFn('Kit (ARM 64-bit)'),
        event: { selected: DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_ARM },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_LINUX,
        kitId: KitIds.DOWNLOAD_UNIFIED_DOWNLOADER_LINUX,
        name: 'Downloader',
        downloadType: DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_LINUX,
        dependencies: {
          feature: null,
          methods: ['isPackageDefaultProduct'],
        },
        disabledMessage: '',
        label: translateFn('Downloader'),
        event: { selected: DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_LINUX },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_LINUX_PRODUCTION_RING,
        kitId: KitIds.DOWNLOAD_UNIFIED_DOWNLOADER_LINUX,
        name: 'Downloader for Production ring',
        downloadType: DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_LINUX_PRODUCTION_RING,
        dependencies: {
          feature: [Feature.PRODUCT_FEATURE_RING_STAGING_VISIBLE],
          methods: ['isPackageDefaultProduct'],
          parameters: null,
        },
        disabledMessage: this.unavailableMessage,
        label: translateFn('Downloader for Production ring'),
        event: { selected: DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_LINUX_PRODUCTION_RING },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_LINUX_TEST1_RING,
        kitId: KitIds.DOWNLOAD_UNIFIED_DOWNLOADER_LINUX,
        name: 'Downloader for Test ring 1',
        downloadType: DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_LINUX_TEST1_RING,
        dependencies: {
          feature: [Feature.PRODUCT_FEATURE_RING_STAGING_VISIBLE],
          methods: ['isPackageDefaultProduct'],
          parameters: null,
        },
        disabledMessage: this.unavailableMessage,
        label: translateFn('Downloader for Test ring 1'),
        event: { selected: DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_LINUX_TEST1_RING },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_LINUX_TEST2_RING,
        kitId: KitIds.DOWNLOAD_UNIFIED_DOWNLOADER_LINUX,
        name: 'Downloader for Test ring 2',
        downloadType: DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_LINUX_TEST2_RING,
        dependencies: {
          feature: [Feature.PRODUCT_FEATURE_RING_STAGING_VISIBLE],
          methods: ['isPackageDefaultProduct'],
          parameters: null,
        },
        disabledMessage: this.unavailableMessage,
        label: translateFn('Downloader for Test ring 2'),
        event: { selected: DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_LINUX_TEST2_RING },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_NIX_x32,
        kitId: KitIds.DOWNLOAD_UNIFIED_FULL_KIT_NIX_x32,
        name: 'Kit (Intel, AMD x86 32-bit)',
        downloadType: DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_NIX_x32,
        dependencies: {
          feature: null,
          methods: ['isPackageDefaultProduct'],
        },
        disabledMessage: '',
        label: translateFn('Kit (Intel, AMD x86 32-bit)'),
        event: { selected: DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_NIX_x32 },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_NIX_x64,
        kitId: KitIds.DOWNLOAD_UNIFIED_FULL_KIT_NIX_x64,
        name: 'Kit (Intel, AMD x86 64-bit)',
        downloadType: DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_NIX_x64,
        dependencies: {
          feature: null,
          methods: ['isPackageDefaultProduct'],
        },
        disabledMessage: '',
        label: translateFn('Kit (Intel, AMD x86 64-bit)'),
        event: { selected: DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_NIX_x64 },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_NIX_ARM,
        kitId: KitIds.DOWNLOAD_UNIFIED_FULL_KIT_NIX_ARM,
        name: 'Kit (ARM 64-bit)',
        downloadType: DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_NIX_ARM,
        dependencies: {
          feature: null,
          methods: ['isPackageDefaultProduct'],
        },
        disabledMessage: '',
        label: translateFn('Kit (ARM 64-bit)'),
        event: { selected: DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_NIX_ARM },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_SECURITY_CONTAINER,
        kitId: KitIds.DOWNLOAD_SECURITY_CONTAINER,
        name: 'Security Container',
        downloadType: DownloadTypes.DOWNLOAD_SECURITY_CONTAINER,
        dependencies: {
          feature: Feature.PRODUCT_FEATURE_CONTAINER_PROTECTION_VISIBLE,
          methods: ['isPackageDefaultProduct', 'packageCompanyHasContainerProtectionUsable'],
        },
        disabledMessage: '',
        label: translateFn('Security Container'),
        event: { selected: DownloadTypes.DOWNLOAD_SECURITY_CONTAINER },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_MAC,
        kitId: KitIds.DOWNLOAD_UNIFIED_DOWNLOADER_MAC,
        name: 'Downloader',
        downloadType: DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_MAC,
        dependencies: {
          feature: Feature.PRODUCT_FEATURE_EPS_SERVICE,
          methods: ['isPackageDefaultProduct'],
        },
        disabledMessage: '',
        label: translateFn('Downloader'),
        event: { selected: DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_MAC },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_MAC_PRODUCTION_RING,
        kitId: KitIds.DOWNLOAD_UNIFIED_DOWNLOADER_MAC,
        name: 'Downloader for Production ring',
        downloadType: DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_MAC_PRODUCTION_RING,
        dependencies: {
          feature: [Feature.PRODUCT_FEATURE_EPS_SERVICE, Feature.PRODUCT_FEATURE_RING_STAGING_VISIBLE],
          methods: ['isPackageDefaultProduct'],
          parameters: null,
        },
        disabledMessage: this.unavailableMessage,
        label: translateFn('Downloader for Production ring'),
        event: { selected: DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_MAC_PRODUCTION_RING },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_MAC_TEST1_RING,
        kitId: KitIds.DOWNLOAD_UNIFIED_DOWNLOADER_MAC,
        name: 'Downloader for Test ring 1',
        downloadType: DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_MAC_TEST1_RING,
        dependencies: {
          feature: [Feature.PRODUCT_FEATURE_EPS_SERVICE, Feature.PRODUCT_FEATURE_RING_STAGING_VISIBLE],
          methods: ['isPackageDefaultProduct'],
          parameters: null,
        },
        disabledMessage: this.unavailableMessage,
        label: translateFn('Downloader for Test ring 1'),
        event: { selected: DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_MAC_TEST1_RING },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_MAC_TEST2_RING,
        kitId: KitIds.DOWNLOAD_UNIFIED_DOWNLOADER_MAC,
        name: 'Downloader for Test ring 2',
        downloadType: DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_MAC_TEST2_RING,
        dependencies: {
          feature: [Feature.PRODUCT_FEATURE_EPS_SERVICE, Feature.PRODUCT_FEATURE_RING_STAGING_VISIBLE],
          methods: ['isPackageDefaultProduct'],
          parameters: null,
        },
        disabledMessage: this.unavailableMessage,
        label: translateFn('Downloader for Test ring 2'),
        event: { selected: DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_MAC_TEST2_RING },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_MAC_FULL_KIT_X86,
        kitId: KitIds.DOWNLOAD_MAC_FULL_KIT_X86,
        name: 'Kit (Intel x86)',
        downloadType: DownloadTypes.DOWNLOAD_MAC_FULL_KIT_X86,
        dependencies: {
          feature: Feature.PRODUCT_FEATURE_EPS_SERVICE,
          methods: ['isPackageDefaultProduct'],
        },
        disabledMessage: '',
        label: translateFn('Kit (Intel x86)'),
        event: { selected: DownloadTypes.DOWNLOAD_MAC_FULL_KIT_X86 },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_MAC_FULL_KIT_ARM,
        kitId: KitIds.DOWNLOAD_MAC_FULL_KIT_ARM,
        name: 'Kit (Apple M Series)',
        downloadType: DownloadTypes.DOWNLOAD_MAC_FULL_KIT_ARM,
        dependencies: {
          feature: Feature.PRODUCT_FEATURE_EPS_SERVICE,
          methods: ['isPackageDefaultProduct'],
        },
        disabledMessage: '',
        label: translateFn('Kit (Apple M Series)'),
        event: { selected: DownloadTypes.DOWNLOAD_MAC_FULL_KIT_ARM },
        disabled: false,
      },
    ]);
  }

  private setSVaKits(): void {
    this.packageTypeToKits.set(PackageType.SVA, [
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_SVA_VMWARE_WITHOUT_VCENTER_OVA,
        kitId: KitIds.DOWNLOAD_SVA_VMWARE_WITHOUT_VCENTER_OVA,
        name: 'Security Server (ESXi standalone)',
        downloadType: DownloadTypes.DOWNLOAD_SVA_VMWARE_WITHOUT_VCENTER_OVA,
        dependencies: {
          feature: Feature.PRODUCT_FEATURE_REMOTE_ENGINES_SCANNING,
          methods: null,
        },
        disabledMessage: '',
        label: translateFn('Security Server (ESXi standalone)'),
        event: { selected: DownloadTypes.DOWNLOAD_SVA_VMWARE_WITHOUT_VCENTER_OVA },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_SVA_VMWARE_OVA,
        kitId: KitIds.DOWNLOAD_SVA_VMWARE_OVA,
        name: 'VMware (OVA)',
        downloadType: DownloadTypes.DOWNLOAD_SVA_VMWARE_OVA,
        dependencies: {
          feature: Feature.PRODUCT_FEATURE_REMOTE_ENGINES_SCANNING,
          methods: null,
        },
        disabledMessage: '',
        label: translateFn('VMware (OVA)'),
        event: { selected: DownloadTypes.DOWNLOAD_SVA_VMWARE_OVA },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_SVA_CITRIX_XEN_XVA,
        kitId: KitIds.DOWNLOAD_SVA_CITRIX_XEN_XVA,
        name: 'Citrix Xen (XVA)',
        downloadType: DownloadTypes.DOWNLOAD_SVA_CITRIX_XEN_XVA,
        dependencies: {
          feature: Feature.PRODUCT_FEATURE_REMOTE_ENGINES_SCANNING,
          methods: null,
        },
        disabledMessage: '',
        label: translateFn('Citrix Xen (XVA)'),
        event: { selected: DownloadTypes.DOWNLOAD_SVA_CITRIX_XEN_XVA },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_SVA_MICROSOFT_HYPER_V,
        kitId: KitIds.DOWNLOAD_SVA_MICROSOFT_HYPER_V,
        name: 'Microsoft Hyper-V (VHD)',
        downloadType: DownloadTypes.DOWNLOAD_SVA_MICROSOFT_HYPER_V,
        dependencies: {
          feature: Feature.PRODUCT_FEATURE_REMOTE_ENGINES_SCANNING,
          methods: null,
        },
        disabledMessage: '',
        label: translateFn('Microsoft Hyper-V (VHD)'),
        event: { selected: DownloadTypes.DOWNLOAD_SVA_MICROSOFT_HYPER_V },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_SVA_MICROSOFT_HYPER_V_GEN2,
        kitId: KitIds.DOWNLOAD_SVA_MICROSOFT_HYPER_V_GEN2,
        name: 'Microsoft Hyper-V Gen 2 (VHDX)',
        downloadType: DownloadTypes.DOWNLOAD_SVA_MICROSOFT_HYPER_V_GEN2,
        dependencies: {
          feature: Feature.PRODUCT_FEATURE_REMOTE_ENGINES_SCANNING,
          methods: null,
        },
        disabledMessage: '',
        label: translateFn('Microsoft Hyper-V Gen 2 (VHDX)'),
        event: { selected: DownloadTypes.DOWNLOAD_SVA_MICROSOFT_HYPER_V_GEN2 },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_SVA_NUTANIX_VMDK,
        kitId: KitIds.DOWNLOAD_SVA_NUTANIX_VMDK,
        name: 'Nutanix Prism (VMDK)',
        downloadType: DownloadTypes.DOWNLOAD_SVA_NUTANIX_VMDK,
        dependencies: {
          feature: Feature.PRODUCT_FEATURE_REMOTE_ENGINES_SCANNING,
          methods: null,
        },
        disabledMessage: '',
        label: translateFn('Nutanix Prism (VMDK)'),
        event: { selected: DownloadTypes.DOWNLOAD_SVA_NUTANIX_VMDK },
        disabled: false,
      },
    ]);
  }

  private setNSVAKits(): void {
    this.packageTypeToKits.set(PackageType.NSVA, [
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_NSVA_VMWARE_OVA,
        kitId: KitIds.DOWNLOAD_NSVA_VMWARE_OVA,
        name: 'VMware (OVA)',
        downloadType: DownloadTypes.DOWNLOAD_NSVA_VMWARE_OVA,
        dependencies: {
          feature: null,
          methods: ['hasAccessToNSVAPackages'],
        },
        disabledMessage: '',
        label: translateFn('VMware (OVA)'),
        event: { selected: DownloadTypes.DOWNLOAD_NSVA_VMWARE_OVA },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_NSVA_MICROSOFT_HYPER_V,
        kitId: KitIds.DOWNLOAD_NSVA_MICROSOFT_HYPER_V,
        name: 'Microsoft Hyper-V (VHD)',
        downloadType: DownloadTypes.DOWNLOAD_NSVA_MICROSOFT_HYPER_V,
        dependencies: {
          feature: null,
          methods: ['hasAccessToNSVAPackages'],
        },
        disabledMessage: '',
        label: translateFn('Microsoft Hyper-V (VHD)'),
        event: { selected: DownloadTypes.DOWNLOAD_NSVA_MICROSOFT_HYPER_V },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_NSVA_MICROSOFT_HYPER_V_GEN2,
        kitId: KitIds.DOWNLOAD_NSVA_MICROSOFT_HYPER_V_GEN2,
        name: 'Microsoft Hyper-V Gen 2 (VHDX)',
        downloadType: DownloadTypes.DOWNLOAD_NSVA_MICROSOFT_HYPER_V_GEN2,
        dependencies: {
          feature: null,
          methods: ['hasAccessToNSVAPackages'],
        },
        disabledMessage: '',
        label: translateFn('Microsoft Hyper-V Gen 2 (VHDX)'),
        event: { selected: DownloadTypes.DOWNLOAD_NSVA_MICROSOFT_HYPER_V_GEN2 },
        disabled: false,
      },
    ]);
  }

  private setSandboxKits(): void {
    this.packageTypeToKits.set(PackageType.Sandbox, [
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_SANDBOX_ANALYZER,
        kitId: KitIds.DOWNLOAD_SANDBOX_ANALYZER,
        name: 'Security Appliance (ESXi standalone)',
        downloadType: DownloadTypes.DOWNLOAD_SANDBOX_ANALYZER,
        dependencies: {
          feature: Feature.PRODUCT_FEATURE_GHOSTR_VISIBLE,
          methods: null,
        },
        disabledMessage: '',
        label: translateFn('Security Appliance (ESXi standalone)'),
        event: { selected: DownloadTypes.DOWNLOAD_SANDBOX_ANALYZER },
        disabled: false,
      },
    ]);
  }

  private setGhostKits(): void {
    this.packageTypeToKits.set(
      PackageType.Ghostr,

      [
        {
          id: 'download-type-' + DownloadTypes.DOWNLOAD_GHOSTR_SENSOR,
          kitId: KitIds.DOWNLOAD_GHOSTR_SENSOR,
          name: 'VMware (OVA)',
          downloadType: DownloadTypes.DOWNLOAD_GHOSTR_SENSOR,
          dependencies: {
            feature: Feature.PRODUCT_FEATURE_GHOSTR_VISIBLE,
            methods: null,
          },
          disabledMessage: '',
          label: translateFn('VMware (OVA)'),
          event: { selected: DownloadTypes.DOWNLOAD_GHOSTR_SENSOR },
          disabled: false,
        },
      ]
    );
  }

  private setFratKits(packageFratType: PackageType): void {
    this.packageTypeToKits.set(packageFratType, [
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_WIN,
        kitId: KitIds.DOWNLOAD_UNIFIED_DOWNLOADER_WIN,
        name: 'Windows Downloader',
        downloadType: DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_WIN,
        dependencies: {
          feature: null,
          methods: null,
        },
        disabledMessage: '',
        label: translateFn('Windows Downloader'),
        event: { selected: DownloadTypes.DOWNLOAD_UNIFIED_DOWNLOADER_WIN },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_WIN32,
        kitId: KitIds.DOWNLOAD_UNIFIED_FULL_KIT_WIN32,
        name: 'Windows kit (Intel, AMD x86 32-bit)',
        downloadType: DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_WIN32,
        dependencies: {
          feature: null,
          methods: null,
        },
        disabledMessage: '',
        label: translateFn('Windows kit (Intel, AMD x86 32-bit)'),
        event: { selected: DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_WIN32 },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_x64,
        kitId: KitIds.DOWNLOAD_UNIFIED_FULL_KIT_x64,
        name: 'Windows kit (Intel, AMD x86 64-bit)',
        downloadType: DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_x64,
        dependencies: {
          feature: null,
          methods: null,
        },
        disabledMessage: '',
        label: translateFn('Windows kit (Intel, AMD x86 64-bit)'),
        event: { selected: DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_x64 },
        disabled: false,
      },
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_ARM,
        kitId: KitIds.DOWNLOAD_UNIFIED_FULL_KIT_ARM,
        name: 'Windows kit (ARM 64-bit)',
        downloadType: DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_ARM,
        dependencies: {
          feature: null,
          methods: null,
        },
        disabledMessage: '',
        label: translateFn('Windows kit (ARM 64-bit)'),
        event: { selected: DownloadTypes.DOWNLOAD_UNIFIED_FULL_KIT_ARM },
        disabled: false,
      },
    ]);
  }

  private setKubernetesKits(): void {
    this.packageTypeToKits.set(PackageType.K8s, [
      {
        id: 'download-type-' + DownloadTypes.DOWNLOAD_K8S_AGENT,
        kitId: KitIds.DOWNLOAD_K8S_AGENT,
        name: 'Kubernetes agent',
        downloadType: DownloadTypes.DOWNLOAD_K8S_AGENT,
        dependencies: {
          feature: Feature.PRODUCT_FEATURE_KSPM_USABLE,
          methods: null,
        },
        disabledMessage: '',
        label: 'Kubernetes agent',
        event: { selected: DownloadTypes.DOWNLOAD_K8S_AGENT },
        disabled: false,
      },
    ]);
  }
}
